import type { ReactNode } from "react";
import { Headphones, Map as MapIcon } from "lucide-react";

export type CountKey = "photos" | "entities" | "itineraries" | "essays" | "neighborhoods" | "audioguides";

/** Conteggi passati alle voci di menu: non tutte le chiavi arrivano dalla stessa fonte. */
export type NavCounts = Partial<Record<CountKey, number>>;

export interface NavMenuItem {
  key: string;
  labelKey: string;
  path: string;
  countKey?: CountKey;
  /** Voce visibile solo se esistono audioguide per la lingua corrente. */
  requiresAudioguides?: boolean;
  /** Icona opzionale accanto alla label. */
  icon?: ReactNode;
}

export interface NavMenu {
  key: string;
  triggerKey: string;
  activePaths: string[];
  items: NavMenuItem[];
}

export interface NavLink {
  key: string;
  labelKey: string;
  path: string;
  /** Voce visibile solo in italiano: il quiz non ha rotte localizzate. */
  italianOnly?: boolean;
  /** Mostra l'etichetta "NEW" accanto alla voce. */
  isNew?: boolean;
}

export type NavEntry = { kind: "menu"; menu: NavMenu } | { kind: "link"; link: NavLink };

// Menu raggruppato per intento: Archivio fotografico (link diretto, prima voce),
// Luoghi (dove), Racconti (da leggere o ascoltare). Desktop, menu mobile e footer leggono tutti da qui.
// Le audioguide sono schede con audio (route /schede/audioguide): stanno nei Racconti.
export const PLACES_MENU: NavMenu = {
  key: "places",
  triggerKey: "nav.places",
  activePaths: ["/schede", "/rioni", "/map"],
  items: [
    { key: "entities", labelKey: "nav.entities", path: "/schede", countKey: "entities" },
    { key: "neighborhoods", labelKey: "nav.neighborhoods", path: "/rioni", countKey: "neighborhoods" },
    { key: "map", labelKey: "nav.map", path: "/map", icon: <MapIcon className="h-4 w-4" /> },
  ],
};

export const STORIES_MENU: NavMenu = {
  key: "stories",
  triggerKey: "nav.stories",
  activePaths: ["/approfondimenti", "/itinerari", "/schede/audioguide"],
  items: [
    { key: "essays", labelKey: "nav.essays", path: "/approfondimenti", countKey: "essays" },
    { key: "itineraries", labelKey: "nav.itineraries", path: "/itinerari", countKey: "itineraries" },
    { key: "audioguide", labelKey: "nav.audioguide", path: "/schede/audioguide", countKey: "audioguides", requiresAudioguides: true, icon: <Headphones className="h-4 w-4" /> },
  ],
};

export const MAIN_NAV: NavEntry[] = [
  { kind: "link", link: { key: "catalog", labelKey: "nav.catalog", path: "/archivio" } },
  { kind: "menu", menu: PLACES_MENU },
  { kind: "menu", menu: STORIES_MENU },
  { kind: "link", link: { key: "quiz", labelKey: "nav.quiz", path: "/quiz", italianOnly: true, isNew: true } },
  { kind: "link", link: { key: "about", labelKey: "nav.about", path: "/about" } },
];

export interface LanguageDef {
  code: "it" | "en" | "de" | "sl";
  label: string;
  display: string;
  flag: ReactNode;
}

// Bandiere SVG inline 20x20 portate ESATTE da ChakraHeader (:404-410 / :431-473).
// Display 'DE/AT' per de come da sorgente (:414, :463).
export const LANGUAGES: LanguageDef[] = [
  {
    code: "it",
    label: "Italiano",
    display: "IT",
    flag: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32">
        <path fill="#fff" d="M10 4H22V28H10z"></path>
        <path d="M5,4h6V28H5c-2.208,0-4-1.792-4-4V8c0-2.208,1.792-4,4-4Z" fill="#41914d"></path>
        <path d="M25,4h6V28h-6c-2.208,0-4-1.792-4-4V8c0-2.208,1.792-4,4-4Z" transform="rotate(180 26 16)" fill="#bf393b"></path>
        <path d="M27,4H5c-2.209,0-4,1.791-4,4V24c0,2.209,1.791,4,4,4H27c2.209,0,4-1.791,4-4V8c0-2.209-1.791-4-4-4Zm3,20c0,1.654-1.346,3-3,3H5c-1.654,0-3-1.346-3-3V8c0-1.654,1.346-3,3-3H27c1.654,0,3,1.346,3,3V24Z" opacity=".15"></path>
        <path d="M27,5H5c-1.657,0-3,1.343-3,3v1c0-1.657,1.343-3,3-3H27c1.657,0,3,1.343,3,3v-1c0-1.657-1.343-3-3-3Z" fill="#fff" opacity=".2"></path>
      </svg>
    ),
  },
  {
    code: "en",
    label: "English",
    display: "EN",
    flag: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32">
        <rect x="1" y="4" width="30" height="24" rx="4" ry="4" fill="#071b65"></rect>
        <path d="M5.101,4h-.101c-1.981,0-3.615,1.444-3.933,3.334L26.899,28h.101c1.981,0,3.615-1.444,3.933-3.334L5.101,4Z" fill="#fff"></path>
        <path d="M22.25,19h-2.5l9.934,7.947c.387-.353,.704-.777,.929-1.257l-8.363-6.691Z" fill="#b92932"></path>
        <path d="M1.387,6.309l8.363,6.691h2.5L2.316,5.053c-.387,.353-.704,.777-.929,1.257Z" fill="#b92932"></path>
        <path d="M5,28h.101L30.933,7.334c-.318-1.891-1.952-3.334-3.933-3.334h-.101L1.067,24.666c.318,1.891,1.952,3.334,3.933,3.334Z" fill="#fff"></path>
        <rect x="13" y="4" width="6" height="24" fill="#fff"></rect>
        <rect x="1" y="13" width="30" height="6" fill="#fff"></rect>
        <rect x="14" y="4" width="4" height="24" fill="#b92932"></rect>
        <rect x="14" y="1" width="4" height="30" transform="translate(32) rotate(90)" fill="#b92932"></rect>
        <path d="M28.222,4.21l-9.222,7.376v1.414h.75l9.943-7.94c-.419-.384-.918-.671-1.471-.85Z" fill="#b92932"></path>
        <path d="M2.328,26.957c.414,.374,.904,.656,1.447,.832l9.225-7.38v-1.408h-.75L2.328,26.957Z" fill="#b92932"></path>
        <path d="M27,4H5c-2.209,0-4,1.791-4,4V24c0,2.209,1.791,4,4,4H27c2.209,0,4-1.791,4-4V8c0-2.209-1.791-4-4-4Zm3,20c0,1.654-1.346,3-3,3H5c-1.654,0-3-1.346-3-3V8c0-1.654,1.346-3,3-3H27c1.654,0,3,1.346,3,3V24Z" opacity=".15"></path>
        <path d="M27,5H5c-1.657,0-3,1.343-3,3v1c0-1.657,1.343-3,3-3H27c1.657,0,3,1.343,3,3v-1c0-1.657-1.343-3-3-3Z" fill="#fff" opacity=".2"></path>
      </svg>
    ),
  },
  {
    code: "de",
    label: "Deutsch",
    display: "DE/AT",
    flag: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32">
        <defs>
          <clipPath id="deNavLeft">
            <rect x="1" y="4" width="15" height="24" rx="4" ry="4" />
          </clipPath>
          <clipPath id="deNavRight">
            <rect x="16" y="4" width="15" height="24" rx="4" ry="4" />
          </clipPath>
        </defs>
        <g clipPath="url(#deNavLeft)">
          <rect x="1" y="4" width="15" height="8" fill="#000" />
          <rect x="1" y="12" width="15" height="8" fill="#cc2b1d" />
          <rect x="1" y="20" width="15" height="8" fill="#f8d147" />
        </g>
        <g clipPath="url(#deNavRight)">
          <rect x="16" y="4" width="15" height="8" fill="#ed2939" />
          <rect x="16" y="12" width="15" height="8" fill="#fff" />
          <rect x="16" y="20" width="15" height="8" fill="#ed2939" />
        </g>
        <line x1="16" y1="4" x2="16" y2="28" stroke="rgba(0,0,0,0.2)" strokeWidth="0.5" />
        <rect x="1" y="4" width="30" height="24" rx="4" ry="4" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
      </svg>
    ),
  },
  {
    code: "sl",
    label: "Slovenščina",
    display: "SL",
    flag: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32">
        <path fill="#0600f5" d="M1 11H31V21H1z"></path>
        <path d="M5,4H27c2.208,0,4,1.792,4,4v4H1v-4c0-2.208,1.792-4,4-4Z" fill="#fff"></path>
        <path d="M5,20H27c2.208,0,4,1.792,4,4v4H1v-4c0-2.208,1.792-4,4-4Z" transform="rotate(180 16 24)" fill="#ea3323"></path>
        <path d="M27,4H5c-2.209,0-4,1.791-4,4V24c0,2.209,1.791,4,4,4H27c2.209,0,4-1.791,4-4V8c0-2.209-1.791-4-4-4Zm3,20c0,1.654-1.346,3-3,3H5c-1.654,0-3-1.346-3-3V8c0-1.654,1.346-3,3-3H27c1.654,0,3,1.346,3,3V24Z" opacity=".15"></path>
        <path d="M27,5H5c-1.657,0-3,1.343-3,3v1c0-1.657,1.343-3,3-3H27c1.657,0,3,1.343,3,3v-1c0-1.657-1.343-3-3-3Z" fill="#fff" opacity=".2"></path>
        <path d="M6.612,8.569c2.019-.889,4.318-.889,6.337,0,.331,4.68-.725,7.333-3.168,7.958-2.443-.625-3.499-3.278-3.168-7.958Z" fill="#0600f5"></path>
        <path d="M9.781,10.646l.767,1.534,.511-.682,1.278,1.705c0,.079-.011,.158-.033,.234-.356,1.245-1.299,2.237-2.524,2.656-1.225-.419-2.168-1.411-2.524-2.656-.022-.076-.033-.155-.033-.234l1.278-1.705,.511,.682,.767-1.534Z" fill="#fff"></path>
        <path d="M6.372,8.68c.08-.038,.16-.075,.241-.111l.253,4.044c.108,1.728,1.266,3.213,2.915,3.74,1.65-.527,2.807-2.012,2.915-3.74l.253-4.044c.081,.036,.161,.073,.241,.111l-.247,3.949c-.116,1.859-1.378,3.448-3.162,3.984-1.784-.535-3.046-2.125-3.162-3.984l-.247-3.949Z" fill="#ea3323"></path>
      </svg>
    ),
  },
];
